/**
 * Niches for the "nicho" step ("¿Qué vendes?").
 *
 * Listed by how many stores sell in each niche (most common first; "Otro" last). Each niche is saved
 * as an option of the backend onboarding question 1 (GraphQL `onboarding`, `on_boarding_options` at
 * signup). Option ids differ per environment but the `order` is the same, so `order` is the backend
 * option's order (not the display position) and the id is read at runtime. Orders 26–36 are the
 * niches added in Nov 2026 (10x-api migration 009); where they don't exist yet the niche still goes
 * to Mixpanel, just without the backend option. Order 21 ("Artículos de hogar") is now part of hogar.
 *
 * Each niche also gives:
 * - `productKey`: business key for the product examples (`SignUpV2.categories.<key>.*` in es.json).
 * - `suggestions`: the categories offered in the next step, each with suggested subcategories.
 * Labels are inline per language (es / en / pt) so a niche and its suggestions live in one place.
 */

const s = (es, en, pt, subs = []) => ({ label: { es, en, pt }, subs });
const sub = (es, en, pt) => ({ es, en, pt });

export const NICHES = [
    {
        order: 1,
        key: 'moda',
        icon: 'ri-t-shirt-line',
        productKey: 'ropa',
        label: { es: 'Ropa y moda', en: 'Clothing & fashion', pt: 'Roupas e moda' },
        suggestions: [
            s('Ropa de mujer', "Women's clothing", 'Roupa feminina', [
                sub('Blusas', 'Blouses', 'Blusas'),
                sub('Vestidos', 'Dresses', 'Vestidos'),
                sub('Pantalones', 'Pants', 'Calças'),
                sub('Faldas', 'Skirts', 'Saias'),
            ]),
            s('Ropa de hombre', "Men's clothing", 'Roupa masculina', [
                sub('Camisas', 'Shirts', 'Camisas'),
                sub('Camisetas', 'T-shirts', 'Camisetas'),
                sub('Pantalones', 'Pants', 'Calças'),
            ]),
            s('Accesorios', 'Accessories', 'Acessórios', [
                sub('Bolsos', 'Bags', 'Bolsas'),
                sub('Gorras', 'Caps', 'Bonés'),
                sub('Cinturones', 'Belts', 'Cintos'),
            ]),
            s('Ropa deportiva', 'Sportswear', 'Roupa esportiva'),
            s('Ofertas', 'Sale', 'Ofertas'),
        ],
    },
    {
        order: 2,
        key: 'belleza',
        icon: 'ri-sparkling-2-line',
        productKey: 'belleza',
        label: {
            es: 'Belleza, maquillaje y perfumería',
            en: 'Beauty, makeup & fragrances',
            pt: 'Beleza, maquiagem e perfumaria',
        },
        suggestions: [
            s('Maquillaje', 'Makeup', 'Maquiagem', [
                sub('Rostro', 'Face', 'Rosto'),
                sub('Ojos', 'Eyes', 'Olhos'),
                sub('Labios', 'Lips', 'Lábios'),
            ]),
            s('Perfumes', 'Fragrances', 'Perfumes', [
                sub('Mujer', 'Women', 'Feminino'),
                sub('Hombre', 'Men', 'Masculino'),
            ]),
            s('Cuidado de la piel', 'Skin care', 'Cuidados com a pele', [
                sub('Limpiadores', 'Cleansers', 'Limpeza'),
                sub('Hidratantes', 'Moisturizers', 'Hidratantes'),
                sub('Protector solar', 'Sunscreen', 'Protetor solar'),
            ]),
            s('Cabello', 'Hair', 'Cabelo', [
                sub('Shampoo', 'Shampoo', 'Shampoo'),
                sub('Tratamientos', 'Treatments', 'Tratamentos'),
            ]),
            s('Uñas', 'Nails', 'Unhas'),
        ],
    },
    {
        order: 26,
        key: 'multimarca',
        icon: 'ri-store-2-line',
        productKey: 'ropa',
        label: { es: 'Tienda multimarca', en: 'Multi-brand store', pt: 'Loja multimarcas' },
        suggestions: [
            s('Ropa', 'Clothing', 'Roupas', [
                sub('Mujer', 'Women', 'Feminino'),
                sub('Hombre', 'Men', 'Masculino'),
            ]),
            s('Calzado', 'Footwear', 'Calçados'),
            s('Accesorios', 'Accessories', 'Acessórios'),
            s('Belleza', 'Beauty', 'Beleza'),
            s('Novedades', 'New arrivals', 'Novidades'),
        ],
    },
    {
        order: 27,
        key: 'variedades',
        icon: 'ri-shopping-basket-line',
        productKey: 'papeleria',
        label: { es: 'Variedades y regalos', en: 'Variety & gifts', pt: 'Variedades e presentes' },
        suggestions: [
            s('Regalos', 'Gifts', 'Presentes', [
                sub('Para ella', 'For her', 'Para ela'),
                sub('Para él', 'For him', 'Para ele'),
            ]),
            s('Detalles', 'Small gifts', 'Lembrancinhas'),
            s('Hogar', 'Home', 'Casa'),
            s('Accesorios', 'Accessories', 'Acessórios'),
            s('Temporada', 'Seasonal', 'Temporada'),
        ],
    },
    {
        order: 3,
        key: 'calzado',
        icon: 'ri-footprint-line',
        productKey: 'ropa',
        label: { es: 'Calzado', en: 'Footwear', pt: 'Calçados' },
        suggestions: [
            s('Mujer', 'Women', 'Feminino', [
                sub('Tenis', 'Sneakers', 'Tênis'),
                sub('Sandalias', 'Sandals', 'Sandálias'),
                sub('Tacones', 'Heels', 'Saltos'),
            ]),
            s('Hombre', 'Men', 'Masculino', [
                sub('Tenis', 'Sneakers', 'Tênis'),
                sub('Formal', 'Dress shoes', 'Social'),
                sub('Botas', 'Boots', 'Botas'),
            ]),
            s('Niños', 'Kids', 'Infantil'),
            s('Deportivo', 'Sports', 'Esportivo'),
            s('Accesorios', 'Accessories', 'Acessórios'),
        ],
    },
    {
        order: 28,
        key: 'joyeria',
        icon: 'ri-vip-diamond-line',
        productKey: 'accesorios',
        label: { es: 'Joyería, bisutería y relojería', en: 'Jewelry & watches', pt: 'Joias, bijuterias e relógios' },
        suggestions: [
            s('Joyería', 'Fine jewelry', 'Joias', [
                sub('Anillos', 'Rings', 'Anéis'),
                sub('Cadenas', 'Chains', 'Correntes'),
                sub('Aretes', 'Earrings', 'Brincos'),
            ]),
            s('Bisutería', 'Fashion jewelry', 'Bijuterias', [
                sub('Collares', 'Necklaces', 'Colares'),
                sub('Pulseras', 'Bracelets', 'Pulseiras'),
            ]),
            s('Relojes', 'Watches', 'Relógios', [
                sub('Mujer', 'Women', 'Feminino'),
                sub('Hombre', 'Men', 'Masculino'),
            ]),
            s('Accesorios', 'Accessories', 'Acessórios'),
        ],
    },
    {
        order: 22,
        key: 'deportes',
        icon: 'ri-run-line',
        productKey: 'ropa',
        label: { es: 'Deportes y fitness', en: 'Sports & fitness', pt: 'Esportes e fitness' },
        suggestions: [
            s('Ropa deportiva', 'Sportswear', 'Roupa esportiva', [
                sub('Mujer', 'Women', 'Feminino'),
                sub('Hombre', 'Men', 'Masculino'),
            ]),
            s('Calzado deportivo', 'Sports shoes', 'Calçados esportivos'),
            s('Implementos', 'Equipment', 'Equipamentos'),
            s('Suplementos', 'Supplements', 'Suplementos'),
        ],
    },
    {
        order: 8,
        key: 'juguetes',
        icon: 'ri-gift-line',
        productKey: 'papeleria',
        label: { es: 'Jugueterías y detalles', en: 'Toys & small gifts', pt: 'Brinquedos e lembrancinhas' },
        suggestions: [
            s('Juguetes', 'Toys', 'Brinquedos', [
                sub('Didácticos', 'Educational', 'Educativos'),
                sub('Peluches', 'Plush toys', 'Pelúcias'),
                sub('Juegos de mesa', 'Board games', 'Jogos de tabuleiro'),
            ]),
            s('Detalles', 'Small gifts', 'Lembrancinhas'),
            s('Regalos', 'Gifts', 'Presentes'),
            s('Fiestas', 'Party', 'Festas'),
        ],
    },
    {
        order: 5,
        key: 'alimentos',
        icon: 'ri-restaurant-2-line',
        productKey: 'comida',
        label: {
            es: 'Alimentos y bebidas sin alcohol',
            en: 'Food & non-alcoholic drinks',
            pt: 'Alimentos e bebidas sem álcool',
        },
        suggestions: [
            s('Platos principales', 'Main dishes', 'Pratos principais'),
            s('Bebidas', 'Drinks', 'Bebidas', [
                sub('Jugos', 'Juices', 'Sucos'),
                sub('Gaseosas', 'Sodas', 'Refrigerantes'),
                sub('Calientes', 'Hot drinks', 'Bebidas quentes'),
            ]),
            s('Postres', 'Desserts', 'Sobremesas'),
            s('Snacks', 'Snacks', 'Lanches'),
            s('Combos', 'Combos', 'Combos'),
        ],
    },
    {
        order: 4,
        key: 'hogar',
        icon: 'ri-home-4-line',
        productKey: 'hogar',
        label: { es: 'Hogar, muebles y decoración', en: 'Home, furniture & decor', pt: 'Casa, móveis e decoração' },
        suggestions: [
            s('Muebles', 'Furniture', 'Móveis', [
                sub('Sala', 'Living room', 'Sala'),
                sub('Comedor', 'Dining room', 'Sala de jantar'),
                sub('Habitación', 'Bedroom', 'Quarto'),
            ]),
            s('Decoración', 'Decor', 'Decoração', [
                sub('Cuadros', 'Wall art', 'Quadros'),
                sub('Velas', 'Candles', 'Velas'),
                sub('Plantas', 'Plants', 'Plantas'),
            ]),
            s('Cocina', 'Kitchen', 'Cozinha', [
                sub('Utensilios', 'Utensils', 'Utensílios'),
                sub('Ollas y sartenes', 'Pots & pans', 'Panelas'),
            ]),
            s('Electrodomésticos', 'Appliances', 'Eletrodomésticos'),
            s('Textiles', 'Textiles', 'Têxteis'),
        ],
    },
    {
        order: 19,
        key: 'sexshop',
        icon: 'ri-heart-3-line',
        productKey: 'otro',
        label: { es: 'Sex shop y bienestar sexual', en: 'Adult store & sexual wellness', pt: 'Sex shop e bem-estar sexual' },
        suggestions: [
            s('Juguetes', 'Toys', 'Brinquedos'),
            s('Lencería', 'Lingerie', 'Lingerie'),
            s('Lubricantes', 'Lubricants', 'Lubrificantes'),
            s('Bienestar', 'Wellness', 'Bem-estar'),
            s('Kits', 'Kits', 'Kits'),
        ],
    },
    {
        order: 12,
        key: 'farmacia',
        icon: 'ri-capsule-line',
        productKey: 'salud',
        label: {
            es: 'Salud, farmacias y productos naturales',
            en: 'Health, pharmacy & natural products',
            pt: 'Saúde, farmácias e produtos naturais',
        },
        suggestions: [
            s('Medicamentos', 'Medicines', 'Medicamentos'),
            s('Productos naturales', 'Natural products', 'Produtos naturais'),
            s('Vitaminas y suplementos', 'Vitamins & supplements', 'Vitaminas e suplementos'),
            s('Cuidado personal', 'Personal care', 'Cuidados pessoais'),
            s('Primeros auxilios', 'First aid', 'Primeiros socorros'),
        ],
    },
    {
        order: 10,
        key: 'mascotas',
        icon: 'ri-bear-smile-line',
        productKey: 'mascotas',
        label: { es: 'Mascotas y veterinarias', en: 'Pets & vet', pt: 'Pets e veterinárias' },
        suggestions: [
            s('Perros', 'Dogs', 'Cachorros', [
                sub('Alimento', 'Food', 'Ração'),
                sub('Juguetes', 'Toys', 'Brinquedos'),
                sub('Camas', 'Beds', 'Caminhas'),
            ]),
            s('Gatos', 'Cats', 'Gatos', [
                sub('Alimento', 'Food', 'Ração'),
                sub('Arena', 'Litter', 'Areia'),
                sub('Juguetes', 'Toys', 'Brinquedos'),
            ]),
            s('Accesorios', 'Accessories', 'Acessórios'),
            s('Higiene', 'Grooming', 'Higiene'),
            s('Veterinaria', 'Vet care', 'Veterinária'),
        ],
    },
    {
        order: 6,
        key: 'tecnologia',
        icon: 'ri-smartphone-line',
        productKey: 'tecnologia',
        label: {
            es: 'Tecnología, celulares y accesorios',
            en: 'Tech, phones & accessories',
            pt: 'Tecnologia, celulares e acessórios',
        },
        suggestions: [
            s('Celulares', 'Phones', 'Celulares'),
            s('Accesorios', 'Accessories', 'Acessórios', [
                sub('Cargadores', 'Chargers', 'Carregadores'),
                sub('Fundas', 'Cases', 'Capinhas'),
                sub('Cables', 'Cables', 'Cabos'),
            ]),
            s('Audio', 'Audio', 'Áudio', [
                sub('Audífonos', 'Headphones', 'Fones de ouvido'),
                sub('Parlantes', 'Speakers', 'Caixas de som'),
            ]),
            s('Computadores', 'Computers', 'Computadores'),
            s('Gaming', 'Gaming', 'Games'),
        ],
    },
    {
        order: 29,
        key: 'bebes',
        icon: 'ri-parent-line',
        productKey: 'ropa',
        label: { es: 'Artículos para bebés y niños', en: 'Baby & kids', pt: 'Artigos para bebês e crianças' },
        suggestions: [
            s('Ropa de bebé', 'Baby clothes', 'Roupa de bebê', [
                sub('Recién nacido', 'Newborn', 'Recém-nascido'),
                sub('Bodies', 'Bodysuits', 'Bodies'),
            ]),
            s('Ropa de niños', "Kids' clothes", 'Roupa infantil'),
            s('Accesorios', 'Accessories', 'Acessórios'),
            s('Cuidado e higiene', 'Care & hygiene', 'Cuidados e higiene'),
            s('Juguetes', 'Toys', 'Brinquedos'),
        ],
    },
    {
        order: 30,
        key: 'bolsos',
        icon: 'ri-handbag-line',
        productKey: 'accesorios',
        label: {
            es: 'Bolsos, maletas y marroquinería',
            en: 'Bags, luggage & leather goods',
            pt: 'Bolsas, malas e artigos de couro',
        },
        suggestions: [
            s('Bolsos', 'Handbags', 'Bolsas', [
                sub('Mujer', 'Women', 'Feminino'),
                sub('Hombre', 'Men', 'Masculino'),
            ]),
            s('Morrales', 'Backpacks', 'Mochilas'),
            s('Maletas', 'Luggage', 'Malas'),
            s('Billeteras', 'Wallets', 'Carteiras'),
            s('Cinturones', 'Belts', 'Cintos'),
        ],
    },
    {
        order: 13,
        key: 'turismo',
        icon: 'ri-hotel-line',
        productKey: 'otro',
        label: { es: 'Turismo, viajes y alojamiento', en: 'Travel & lodging', pt: 'Turismo, viagens e hospedagem' },
        suggestions: [
            s('Habitaciones', 'Rooms', 'Quartos'),
            s('Planes', 'Packages', 'Pacotes'),
            s('Tours', 'Tours', 'Passeios'),
            s('Transporte', 'Transport', 'Transporte'),
        ],
    },
    {
        order: 9,
        key: 'vehiculos',
        icon: 'ri-motorbike-line',
        productKey: 'otro',
        label: {
            es: 'Vehículos, repuestos y accesorios',
            en: 'Vehicles, parts & accessories',
            pt: 'Veículos, peças e acessórios',
        },
        suggestions: [
            s('Repuestos', 'Spare parts', 'Peças'),
            s('Accesorios', 'Accessories', 'Acessórios'),
            s('Motos', 'Motorcycles', 'Motos'),
            s('Bicicletas', 'Bicycles', 'Bicicletas'),
            s('Lubricantes', 'Lubricants', 'Lubrificantes'),
        ],
    },
    {
        order: 31,
        key: 'artesanias',
        icon: 'ri-brush-line',
        productKey: 'papeleria',
        label: { es: 'Artesanías y manualidades', en: 'Crafts & handmade', pt: 'Artesanato e trabalhos manuais' },
        suggestions: [
            s('Artesanías', 'Handicrafts', 'Artesanato'),
            s('Hecho a mano', 'Handmade', 'Feito à mão'),
            s('Materiales', 'Supplies', 'Materiais'),
            s('Decoración', 'Decor', 'Decoração'),
        ],
    },
    {
        order: 23,
        key: 'libreria',
        icon: 'ri-book-open-line',
        productKey: 'papeleria',
        label: { es: 'Librerías y papelerías', en: 'Books & stationery', pt: 'Livrarias e papelarias' },
        suggestions: [
            s('Libros', 'Books', 'Livros'),
            s('Papelería', 'Stationery', 'Papelaria'),
            s('Útiles escolares', 'School supplies', 'Material escolar'),
            s('Arte', 'Art supplies', 'Arte'),
        ],
    },
    {
        order: 15,
        key: 'marketing',
        icon: 'ri-megaphone-line',
        productKey: 'otro',
        label: { es: 'Marketing y publicidad', en: 'Marketing & advertising', pt: 'Marketing e publicidade' },
        suggestions: [
            s('Redes sociales', 'Social media', 'Redes sociais'),
            s('Diseño', 'Design', 'Design'),
            s('Publicidad', 'Advertising', 'Publicidade'),
            s('Planes', 'Plans', 'Planos'),
        ],
    },
    {
        order: 24,
        key: 'construccion',
        icon: 'ri-hammer-line',
        productKey: 'otro',
        label: {
            es: 'Ferreterías y materiales de construcción',
            en: 'Hardware & building materials',
            pt: 'Ferragens e materiais de construção',
        },
        suggestions: [
            s('Herramientas', 'Tools', 'Ferramentas'),
            s('Materiales', 'Materials', 'Materiais'),
            s('Eléctricos', 'Electrical', 'Elétricos'),
            s('Plomería', 'Plumbing', 'Hidráulica'),
            s('Pinturas', 'Paint', 'Tintas'),
        ],
    },
    {
        order: 17,
        key: 'optica',
        icon: 'ri-glasses-line',
        productKey: 'accesorios',
        label: { es: 'Ópticas', en: 'Opticians', pt: 'Óticas' },
        suggestions: [
            s('Monturas', 'Frames', 'Armações'),
            s('Gafas de sol', 'Sunglasses', 'Óculos de sol'),
            s('Lentes de contacto', 'Contact lenses', 'Lentes de contato'),
            s('Accesorios', 'Accessories', 'Acessórios'),
        ],
    },
    {
        order: 20,
        key: 'licores',
        icon: 'ri-goblet-line',
        productKey: 'comida',
        label: { es: 'Licores y cervezas', en: 'Liquor & beer', pt: 'Bebidas alcoólicas e cervejas' },
        suggestions: [
            s('Cervezas', 'Beer', 'Cervejas'),
            s('Vinos', 'Wine', 'Vinhos'),
            s('Whisky', 'Whisky', 'Uísque'),
            s('Ron', 'Rum', 'Rum'),
            s('Combos', 'Combos', 'Combos'),
        ],
    },
    {
        order: 32,
        key: 'impresion',
        icon: 'ri-printer-line',
        productKey: 'otro',
        label: {
            es: 'Impresión, estampados y personalización',
            en: 'Printing & custom products',
            pt: 'Impressão, estampas e personalização',
        },
        suggestions: [
            s('Camisetas personalizadas', 'Custom T-shirts', 'Camisetas personalizadas'),
            s('Mugs y termos', 'Mugs & tumblers', 'Canecas e garrafas'),
            s('Impresión', 'Printing', 'Impressão'),
            s('Publicidad', 'Promotional items', 'Brindes'),
        ],
    },
    {
        order: 14,
        key: 'floristeria',
        icon: 'ri-flower-line',
        productKey: 'papeleria',
        label: { es: 'Floristerías', en: 'Florists', pt: 'Floriculturas' },
        suggestions: [
            s('Ramos', 'Bouquets', 'Buquês'),
            s('Arreglos', 'Arrangements', 'Arranjos'),
            s('Plantas', 'Plants', 'Plantas'),
            s('Eventos', 'Events', 'Eventos'),
            s('Detalles', 'Small gifts', 'Lembrancinhas'),
        ],
    },
    {
        order: 16,
        key: 'inmuebles',
        icon: 'ri-building-line',
        productKey: 'otro',
        label: { es: 'Inmobiliarias y bienes raíces', en: 'Real estate', pt: 'Imobiliárias e imóveis' },
        suggestions: [
            s('Venta', 'For sale', 'Venda'),
            s('Arriendo', 'For rent', 'Aluguel'),
            s('Casas', 'Houses', 'Casas'),
            s('Apartamentos', 'Apartments', 'Apartamentos'),
            s('Locales', 'Commercial', 'Comerciais'),
        ],
    },
    {
        order: 11,
        key: 'cursos',
        icon: 'ri-graduation-cap-line',
        productKey: 'otro',
        label: { es: 'Cursos, academias y educación', en: 'Courses, academies & education', pt: 'Cursos, academias e educação' },
        suggestions: [
            s('Cursos', 'Courses', 'Cursos'),
            s('Talleres', 'Workshops', 'Oficinas'),
            s('Mentorías', 'Mentoring', 'Mentorias'),
            s('Material digital', 'Digital content', 'Material digital'),
        ],
    },
    {
        order: 7,
        key: 'servicios',
        icon: 'ri-customer-service-2-line',
        productKey: 'otro',
        label: {
            es: 'Servicios profesionales y técnicos',
            en: 'Professional & technical services',
            pt: 'Serviços profissionais e técnicos',
        },
        suggestions: [
            s('Servicios', 'Services', 'Serviços'),
            s('Paquetes', 'Packages', 'Pacotes'),
            s('Planes', 'Plans', 'Planos'),
            s('Asesorías', 'Consulting', 'Consultorias'),
        ],
    },
    {
        order: 18,
        key: 'finanzas',
        icon: 'ri-bank-line',
        productKey: 'otro',
        label: {
            es: 'Servicios financieros y contables',
            en: 'Financial & accounting services',
            pt: 'Serviços financeiros e contábeis',
        },
        suggestions: [
            s('Contabilidad', 'Accounting', 'Contabilidade'),
            s('Créditos', 'Loans', 'Créditos'),
            s('Seguros', 'Insurance', 'Seguros'),
            s('Asesorías', 'Consulting', 'Consultorias'),
        ],
    },
    {
        order: 33,
        key: 'agricultura',
        icon: 'ri-plant-line',
        productKey: 'hogar',
        label: {
            es: 'Agricultura, jardinería y plantas',
            en: 'Farming, gardening & plants',
            pt: 'Agricultura, jardinagem e plantas',
        },
        suggestions: [
            s('Plantas', 'Plants', 'Plantas'),
            s('Semillas', 'Seeds', 'Sementes'),
            s('Abonos', 'Fertilizers', 'Adubos'),
            s('Herramientas', 'Tools', 'Ferramentas'),
            s('Macetas', 'Pots', 'Vasos'),
        ],
    },
    {
        order: 34,
        key: 'aseo',
        icon: 'ri-drop-line',
        productKey: 'hogar',
        label: { es: 'Productos de aseo y limpieza', en: 'Cleaning supplies', pt: 'Produtos de limpeza' },
        suggestions: [
            s('Limpieza del hogar', 'Home cleaning', 'Limpeza da casa'),
            s('Lavandería', 'Laundry', 'Lavanderia'),
            s('Cuidado personal', 'Personal care', 'Cuidados pessoais'),
            s('Desechables', 'Disposables', 'Descartáveis'),
        ],
    },
    {
        order: 35,
        key: 'eventos',
        icon: 'ri-cake-2-line',
        productKey: 'papeleria',
        label: { es: 'Eventos y fiestas', en: 'Events & parties', pt: 'Eventos e festas' },
        suggestions: [
            s('Decoración', 'Decorations', 'Decoração'),
            s('Globos', 'Balloons', 'Balões'),
            s('Piñatas', 'Piñatas', 'Piñatas'),
            s('Alquiler', 'Rentals', 'Aluguel'),
            s('Recordatorios', 'Party favors', 'Lembrancinhas'),
        ],
    },
    {
        order: 36,
        key: 'musica',
        icon: 'ri-music-2-line',
        productKey: 'tecnologia',
        label: {
            es: 'Instrumentos y equipos musicales',
            en: 'Musical instruments & gear',
            pt: 'Instrumentos e equipamentos musicais',
        },
        suggestions: [
            s('Instrumentos', 'Instruments', 'Instrumentos', [
                sub('Guitarras', 'Guitars', 'Violões e guitarras'),
                sub('Teclados', 'Keyboards', 'Teclados'),
                sub('Percusión', 'Percussion', 'Percussão'),
            ]),
            s('Audio', 'Audio', 'Áudio'),
            s('Accesorios', 'Accessories', 'Acessórios'),
        ],
    },
    {
        order: 25,
        key: 'otro',
        icon: 'ri-more-line',
        productKey: 'otro',
        label: { es: 'Otro', en: 'Other', pt: 'Outro' },
        suggestions: [],
    },
];

/** How many niches show before "Ver más". */
export const NICHE_PRIMARY_COUNT = 8;

/** How many suggestions are added to "Tus categorías" when the categories step opens. */
export const NICHE_SEED_COUNT = 3;

export const getNiche = (key) => NICHES.find((niche) => niche.key === key) || null;

/** Text in the merchant's language (falls back to Spanish). */
export const localized = (text, language) => (text && (text[language] || text.es)) || '';

/** Accent / case insensitive match for the search box. */
const normalize = (text) =>
    String(text || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase();

export const searchNiches = (query, language) => {
    const q = normalize(query).trim();
    if (!q) return NICHES;
    return NICHES.filter((niche) => normalize(localized(niche.label, language)).includes(q));
};

/**
 * Categories (with ids) to offer for a niche in the categories step. Ids are stable per niche so
 * the step can tell a suggestion that is already in "Tus categorías".
 */
export const nicheSuggestions = (key, language) => {
    const niche = getNiche(key);
    if (!niche) return [];
    return niche.suggestions.map((suggestion, index) => ({
        id: `niche-${niche.key}-${index}`,
        label: localized(suggestion.label, language),
        icon: niche.icon,
        subs: suggestion.subs.map((subLabel) => localized(subLabel, language)),
    }));
};

/** Backend option id (this environment) for each niche order: { [order]: id }. */
export const nicheOptionIds = (onboardingQuestions) => {
    const question = (onboardingQuestions || []).find((q) => String(q.id) === '1') || (onboardingQuestions || [])[0];
    const ids = {};
    ((question && question.onboardingOptions) || []).forEach((option) => {
        const id = Number(option.id);
        if (option.order != null && Number.isFinite(id)) ids[option.order] = id;
    });
    return ids;
};

// Loaded once per api and shared by the niche step and the signup (WeakMap: demo and real apis apart).
const optionIdsByApi = new WeakMap();

/** Backend option ids of this environment, `{ [order]: id }` (`{}` when they can't be read). */
export const loadNicheOptionIds = (api) => {
    if (!optionIdsByApi.has(api)) {
        const request = api
            .getOnboardingQuestions()
            .then(nicheOptionIds)
            .catch(() => {
                optionIdsByApi.delete(api); // let the next call retry
                return {};
            });
        optionIdsByApi.set(api, request);
    }
    return optionIdsByApi.get(api);
};

/** Same, without waiting more than `ms` (a slow request never blocks the merchant). */
export const nicheOptionIdsWithin = (api, ms) =>
    Promise.race([loadNicheOptionIds(api), new Promise((resolve) => setTimeout(() => resolve({}), ms))]);
